import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  CreateGigsafeRole,
  FetchGigsafeRolePermissions,
  FetchGigsafeRoles,
  FetchGigsafeUsers,
  InviteGigsafeUser,
  UpdateGigsafeRole,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type {
  CreateGigsafeRolePayload,
  FetchGigsafeRolePermissionsPayload,
  FetchGigsafeRolesPayload,
  FetchGigsafeUsersPayload,
  GigsafePermission,
  GigsafeRole,
  GigsafeUser,
  GigsafeUsersPage,
  InviteGigsafeUserPayload,
  UpdateGigsafeRolePayload,
} from 'src/interfaces/gigsafeUser'
import { flattenAssignablePermissions } from 'src/utils/flattenAssignablePermissions'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { parseDashboardResponse } from 'src/utils/parseDashboardResponse'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

const partnerIdParams = (partnerId: string) => {
  return [{ field: ':partnerId', value: partnerId }]
}

export function* onFetchGigsafeUsers(action: AsyncAction<FetchGigsafeUsersPayload>) {
  try {
    // Flat `?limit=&offset=`: GetGigSafeUsersFilterDto declares `pagination` as a nested DTO but
    // its @Transform reads obj.limit / obj.offset off the TOP LEVEL of the query object.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchGigsafeUsers,
      {
        method: 'GET',
        params: partnerIdParams(action.payload.partnerId),
        query: { limit: action.payload.limit, offset: action.payload.offset },
      },
    )

    // The envelope is `{ data, pagination: { total } }` — the count is NESTED, unlike the
    // webhooks deliveries endpoint. `normalizeApiListPayload` would drop it.
    const { data, total } = parseDashboardResponse<GigsafeUser>(payload)
    const page: GigsafeUsersPage = { users: data, total }

    yield put(FetchGigsafeUsers.success(page))

    if (action.next) {
      action.next(null, page)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.gigsafeUser.onFetchGigsafeUsers] error' })

    yield put(FetchGigsafeUsers.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load users'))
    }
  }
}

export function* onFetchGigsafeRoles(action: AsyncAction<FetchGigsafeRolesPayload>) {
  try {
    // Answers with a raw array, no envelope; the normalizer handles both so this call site stays
    // correct if it is ever wrapped.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchGigsafeRoles,
      { method: 'GET', params: partnerIdParams(action.payload.partnerId) },
    )

    const roles = normalizeApiListPayload<GigsafeRole>(payload)

    yield put(FetchGigsafeRoles.success(roles))

    if (action.next) {
      action.next(null, roles)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.gigsafeUser.onFetchGigsafeRoles] error' })

    yield put(FetchGigsafeRoles.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load roles'))
    }
  }
}

export function* onFetchGigsafeRolePermissions(
  action: AsyncAction<FetchGigsafeRolePermissionsPayload>,
) {
  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminFetchGigsafeRolePermissions,
      { method: 'GET', params: partnerIdParams(action.payload.partnerId) },
    )

    // The route returns `PermissionsAvailableForAssign` verbatim — a grouped OBJECT of 22 keys,
    // not an array. Flattened here so nothing downstream has to know that shape.
    const permissions: GigsafePermission[] = flattenAssignablePermissions(payload)

    yield put(FetchGigsafeRolePermissions.success(permissions))

    if (action.next) {
      action.next(null, permissions)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.gigsafeUser.onFetchGigsafeRolePermissions] error',
    })

    yield put(FetchGigsafeRolePermissions.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load permissions'))
    }
  }
}

export function* onInviteGigsafeUser(action: AsyncAction<InviteGigsafeUserPayload>) {
  const {
    partnerId,
    email,
    firstName,
    lastName,
    roleId,
    allMarkets,
    marketIds = [],
  } = action.payload

  try {
    // `marketIds` must be ABSENT, not empty, when allMarkets is true — the backend's
    // @ValidateIf pair rejects an empty array otherwise. callApi's omitBy only cleans query
    // strings, never JSON bodies, so the key must not be built here in the first place.
    const body: Record<string, unknown> = {
      email,
      firstName,
      lastName,
      roleId,
      allMarkets: Boolean(allMarkets),
    }

    if (!allMarkets) {
      body.marketIds = marketIds
    }

    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminCreateGigsafeInvite,
      { method: 'POST', params: partnerIdParams(partnerId), body },
    )

    yield put(InviteGigsafeUser.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.gigsafeUser.onInviteGigsafeUser] error' })

    yield put(InviteGigsafeUser.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to send the invite'))
    }
  }
}

export function* onCreateGigsafeRole(action: AsyncAction<CreateGigsafeRolePayload>) {
  try {
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminCreateGigsafeRole,
      {
        method: 'POST',
        params: partnerIdParams(action.payload.partnerId),
        body: { name: action.payload.name, permissions: action.payload.permissions },
      },
    )

    yield put(CreateGigsafeRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.gigsafeUser.onCreateGigsafeRole] error' })

    yield put(CreateGigsafeRole.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create the role'))
    }
  }
}

export function* onUpdateGigsafeRole(action: AsyncAction<UpdateGigsafeRolePayload>) {
  try {
    // POST, not PUT — GigsafeUserRolesController declares update as @Post('/:roleId').
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateGigsafeRole,
      {
        method: 'POST',
        params: [
          ...partnerIdParams(action.payload.partnerId),
          { field: ':roleId', value: action.payload.roleId },
        ],
        body: { name: action.payload.name, permissions: action.payload.permissions },
      },
    )

    yield put(UpdateGigsafeRole.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.gigsafeUser.onUpdateGigsafeRole] error' })

    yield put(UpdateGigsafeRole.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update the role'))
    }
  }
}

export function* gigsafeUserSaga() {
  // takeLatest on the reads: the users fetch is re-triggered by every page change, and the two
  // role reads re-run on mount and after each mutation.
  yield takeLatest(FetchGigsafeUsers.type.REQUEST, onFetchGigsafeUsers)
  yield takeLatest(FetchGigsafeRoles.type.REQUEST, onFetchGigsafeRoles)
  yield takeLatest(FetchGigsafeRolePermissions.type.REQUEST, onFetchGigsafeRolePermissions)
  yield takeEvery(InviteGigsafeUser.type.REQUEST, onInviteGigsafeUser)
  yield takeEvery(CreateGigsafeRole.type.REQUEST, onCreateGigsafeRole)
  yield takeEvery(UpdateGigsafeRole.type.REQUEST, onUpdateGigsafeRole)
}
